import { Direction, SortOption } from "@/types/sort";
// hooks/useFamily.ts
import { useDataTableApi } from "@/hooks/useDataTableApi";
import { familyService } from "@/services/family.service";
import { familyTableConfig } from "@/config/family-table.config";
import type { FilterDto } from "@/hooks/filterUtils";
import memberPermissionService from "../services/member-permissions.service";
import { membersService } from "@/services/members.service";
import { FilterOperation, FilterType } from "@/types/filter";

const getAllFamilyMembers = async (
  dto: FilterDto,
  managedIds: string[] | null = null
) => {
  const filters = { ...dto.filters };
  if (Array.isArray(managedIds) && managedIds.length > 0) {
    filters.memberIds = {
      operation: FilterOperation.IN,
      value: managedIds.join(","),
      type: FilterType.STRING,
    };
  }
  // Transform sort if present (array to single object)
  let sort = null;
  if (Array.isArray(dto.sort) && dto.sort.length > 0) {
    const s = dto.sort[0];
    sort = { key: s.key, direction: s.direction as Direction };
  }
  const params = {
    filters,
    page: dto.pagination?.page,
    pageSize: dto.pagination?.pageSize,
    sort,
  };
  const result = await familyService.getFamilyMembers(params);
  return {
    data: result.data,
    total: result.total ?? 0,
  };
};

import { useCallback, useEffect, useState } from "react";

// Ensure all DTOs and calls match backend: no search, skip, take; use sort as {key, direction}
export function getAgeFromDob(
  dobDay?: string | number,
  dobMonth?: string | number,
  dobYear?: string | number
): number | null {
  if (!dobYear || !dobMonth) return null;
  const day = dobDay ? parseInt(dobDay as string, 10) : 1;
  const month = parseInt(dobMonth as string, 10) - 1; // JS months are 0-based
  const year = parseInt(dobYear as string, 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  const dob = new Date(year, month, day);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

export const useFamily = () => {
  const [managedIds, setManagedIds] = useState<string[] | null>(null);
  const [managedLoading, setManagedLoading] = useState(false);
  const [managedError, setManagedError] = useState<string | null>(null);
  // State for current family (name, id, etc)
  const [family, setFamily] = useState<{
    id?: string;
    _id?: string;
    name: string;
  } | null>(null);
  const [familyLoading, setFamilyLoading] = useState(true);
  const [familyError, setFamilyError] = useState<string | null>(null);

  // Helper to get familyId from localStorage member
  // Determine if current user is family owner (fo) or family member (fm)
  let isFamilyOwner = false;
  let isFamilyMember = false;
  let familyId = undefined;
  try {
    const memberStr = localStorage.getItem("member");
    if (memberStr) {
      const member = JSON.parse(memberStr);
      isFamilyOwner = member.family_member_type === "fo";
      isFamilyMember = member.family_member_type === "fm";
      familyId = member.current_family;
    }
  } catch {
    /* noop -- localStorage member payload parse failures are non-fatal */
  }

  // Fetch managed member IDs for family members only
  useEffect(() => {
    if (isFamilyOwner) return; // Don't fetch for family owner
    if (!isFamilyMember) return;
    setManagedLoading(true);
    setManagedError(null);
    memberPermissionService
      .getManagedMembers()
      .then((members) => {
        // Extract IDs from member objects
        const ids = members
          .map((member: any) => member.id || member._id)
          .filter(Boolean);
        setManagedIds(ids);
      })
      .catch((error) => {
        console.error("Error fetching managed members:", error);
        setManagedError("Could not fetch managed member ids.");
        setManagedIds([]);
      })
      .finally(() => setManagedLoading(false));
  }, [isFamilyOwner, isFamilyMember]);

  /**
   * Fetches all family members.
   * - For family owners, fetches all with provided filters.
   * - For family members, fetches only those with permitted managedIds.
   */
  const getAllFamilyMembers = useCallback(async (dto: FilterDto) => {
    // Always fetch all members, regardless of owner/member
    dto.pagination.pageSize = 20;
    const result = await familyService.getFamilyMembers(dto);
    return {
      data: result.data,
      total: result.total ?? 0,
      // managedIds can be used from state for UI logic
    };
  }, []);

  // Fetch family entity by id (auto from localStorage if not provided)
  const fetchFamily = useCallback(async (familyId?: string) => {
    const id =
      familyId ||
      JSON.parse(localStorage.getItem("member") || "{}").current_family;
    if (!id) {
      setFamilyError("Family ID not provided");
      setFamilyLoading(false);
      return;
    }
    setFamilyLoading(true);
    try {
      const fam = await familyService.getFamily();
      setFamily(fam);
      setFamilyError(null);
    } catch {
      setFamilyError("Could not fetch family info");
    } finally {
      setFamilyLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFamily();
  }, [fetchFamily]);

  // Update family name and refetch
  const updateFamilyName = async (newName: string) => {
    const famId = family?._id;
    if (!famId) return;
    await familyService.updateFamily(famId, { name: newName });
    await fetchFamily();
  };

  // DataTable and filters
  const {
    data,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    filters,
    setFilters,
    loading,
    error,
  } = useDataTableApi({
    fetchData: getAllFamilyMembers,
    filterConfig: familyTableConfig.filters,
  });

  // Ensure DataTable does NOT fetch until managedIds is loaded for family members
  useEffect(() => {
    if (isFamilyOwner) return;
    if (!isFamilyMember) return;
    if (managedLoading) return;
    // When managedIds is loaded, re-trigger DataTable fetch by resetting filters
    setFilters((f) => ({ ...f }));
  }, [isFamilyOwner, isFamilyMember, managedLoading, managedIds, setFilters]);

  // Add family member (moves logic from familyListing)
  const addFamilyMember = async (form: any) => {
    if (!(family?._id || family?.id)) throw new Error("No family context");
    // Dynamically import membersService to avoid circular deps
    // Use statically imported membersService
    // const { membersService } = await import('@/services/members.service');
    // Determine member_type based on relationship
    const member_type = Number(form.relationship) === 6 ? "p" : "h";
    return membersService.createMember({
      ...form,
      family_id: family._id || family.id,
      member_type,
      registration_status: form.invite ? "invited" : "not_invited",
    });
  };

  // Fetch managedIds for a specific memberId (by passing memberId as query param)
  const fetchManagedIdsForMember = async (
    memberId: string
  ): Promise<string[]> => {
    try {
      const ids = await memberPermissionService.getManagedMemberIds();
      return ids;
    } catch {
      return [];
    }
  };

  return {
    data,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    filters,
    setFilters,
    loading,
    error,
    family,
    familyLoading,
    familyError,
    updateFamilyName,
    refetchFamily: fetchFamily,
    addFamilyMember,
    managedIds,
    managedLoading,
    managedError,
    isFamilyOwner,
    isFamilyMember,
    fetchManagedIdsForMember,
  };
};
